import type { JournalDoc } from '@beerwright/commands';
import { useNavigate } from '@tanstack/react-router';
import {
  BlendIcon,
  CalendarCheckIcon,
  FlaskConicalIcon,
  MoonIcon,
  NotebookPenIcon,
  PlusIcon,
  Redo2Icon,
  RulerIcon,
  SearchIcon,
  Settings2Icon,
  SunIcon,
  Undo2Icon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useAssistant } from '@/ai/assistant-context';
import { AssistantOrb } from '@/components/assistant-orb';
import { BATCH_STATUS_LABELS } from '@/components/batches/batch-status';
import { useTheme } from '@/components/theme-provider';
import { Button } from '@/components/ui/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { batchMatchesQuery } from '@/lib/batch-search';
import { blendMatchesQuery } from '@/lib/blend-search';
import { journalMatchesQuery } from '@/lib/journal-search';
import { cmd, shiftCmd } from '@/lib/keys';
import { NAV_ITEMS } from '@/lib/nav';
import { userContent } from '@/lib/posthog';
import { recipeCommandKeywords, recipeCommandValue, recipeMatchesQuery } from '@/lib/recipe-search';
import { cn } from '@/lib/utils';
import { useDispatch } from '@/store/use-dispatch';
import {
  useBatchList,
  useBlendList,
  useHistory,
  useJournalList,
  useRecipeList,
  useSettings,
} from '@/store/workspace-context';

/**
 * How many rows the palette gives one kind of thing.
 *
 * The idle recipe preview's number, and now also the ceiling on each of the
 * three searched groups. A palette is a shortcut, and a shortcut whose first
 * screenful is ninety batches has buried Actions and Go to under a word as
 * ordinary as "planned". Twenty is a screenful you scroll once; past that, the
 * kind's own list is the right tool and it is one row away in Go to.
 */
export const PALETTE_RECIPE_PREVIEW = 20;

/** What a journal with no name of its own is called — `createJournal`'s default. */
const UNTITLED_JOURNAL_NAME = 'Untitled journal';

/**
 * What to call a journal on a row.
 *
 * `nameSchema` refuses an empty string, so no command makes a nameless journal
 * and the only ones that exist arrived from somewhere else — an import, an
 * older schema. A row with nothing in it is unclickable, so it borrows the name
 * a journal started without a name is given anyway.
 */
function journalTitle(journal: JournalDoc): string {
  return journal.name.trim().length === 0 ? UNTITLED_JOURNAL_NAME : journal.name;
}

/**
 * A row's identity to cmdk — and, for the three searched kinds, nothing more.
 *
 * Those rows are `forceMount`ed (see the groups below), so cmdk never scores
 * this string: it is only what the row calls itself, which is what keyboard
 * selection reads and what `data-value` carries. Hence a *name*, not a
 * haystack — and the id on the end, because cmdk keys a row by its value and
 * two batches a brewer called "Saison" twice would otherwise be one row, with
 * "selected" lighting up on both.
 */
function commandValue(kind: string, id: string, name: string): string {
  return [kind, name, id].join(' ');
}

/**
 * How anything on screen asks for the palette.
 *
 * A window event rather than a context, because the palette and the button that
 * opens it are not related: the palette is mounted at the foot of the shell and
 * the button is in the nav two levels away, and threading a provider between
 * them would move the shell's structure around for one boolean. The palette
 * owns its own open state either way.
 */
const OPEN_EVENT = 'beerwright:open-command-palette';

/** Open the palette from a click, exactly as ⌘K opens it from a keyboard. */
export function openCommandPalette(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/**
 * The palette's on-screen door, for the devices that have no other one.
 *
 * ⌘K was the only way in, which made jump-to-recipe, undo, redo and the theme
 * switch unreachable for anyone holding a tablet: the phone's nav bar stops at
 * `md`, and from there up the app assumed a keyboard that an iPad does not
 * have. The shortcut is unchanged; this is a second way to the same dialog.
 *
 * **It renders only for a coarse pointer**, which is the honest test for "no
 * keyboard to press ⌘K with" — better than a width, because an iPad in a
 * keyboard case reports a fine pointer and genuinely does have the shortcut,
 * while a 1280 px tablet held in two hands does not. A mouse therefore never
 * sees this button and the nav keeps the shape it had. The caller places it;
 * this component only decides that a cursor does not need it.
 */
export function CommandPaletteTrigger({
  compact = false,
}: {
  /** Icon-only, for the collapsed nav. */
  readonly compact?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size={compact ? 'icon' : 'sm'}
      aria-label="Search and commands"
      title={compact ? `Search and commands (${cmd('K')})` : undefined}
      onClick={openCommandPalette}
      className={cn(
        'pointer-fine:hidden',
        compact ? undefined : 'h-8 w-full justify-start gap-2 px-2',
      )}
    >
      <SearchIcon />
      {/* No ⌘K legend: this button exists only where that key does not. */}
      {!compact && <span>Search</span>}
    </Button>
  );
}

/**
 * ⌘K / Ctrl+K palette — "a pillar of the Linear-like feel, not a nice-to-have"
 * (PLAN §4.1).
 *
 * Jump to any recipe, batch, blend or journal — by name, and by whatever else
 * its own list searches on — create a recipe, ask the assistant, undo/redo
 * through the command log, and flip theme and unit system.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const recipes = useRecipeList();
  const batches = useBatchList();
  const blends = useBlendList();
  const journals = useJournalList();
  const settings = useSettings();
  const history = useHistory();
  const { dispatch, undo, redo } = useDispatch();
  const { resolvedTheme, toggleTheme } = useTheme();
  const assistant = useAssistant();

  const recipeHits = useMemo(() => {
    // Archived recipes are not offered here (PLAN-ARCHIVE §E). The palette is a
    // list, and "not in the list" is what archiving was asked for; a palette
    // that kept suggesting what somebody had put away would be the clutter the
    // feature exists to remove. The archive is reached deliberately, from the
    // Archived chip on the recipes page.
    const live = recipes.filter((recipe) => recipe.archivedAt === null);
    const trimmed = query.trim();
    if (trimmed.length === 0) return live.slice(0, PALETTE_RECIPE_PREVIEW);
    return live.filter((recipe) => recipeMatchesQuery(recipe, trimmed));
  }, [recipes, query]);

  /**
   * The other three kinds: found by their own lists' matchers, capped, and
   * shown only once there is something to have narrowed them.
   *
   * An idle palette is an offer, and the offer is "pick up where you left off",
   * which is what the recipe preview has always been. Four previews would be
   * eighty rows of everything a workspace holds before a single key is pressed —
   * a filing cabinet rather than a shortcut — so these appear only once there is
   * a query. Each stops at {@link PALETTE_RECIPE_PREVIEW}, so no single word can
   * push Actions and Go to off the bottom of the dialog.
   *
   * Nothing is excluded by status: a batch you archived is exactly the one you
   * are typing the name of to find again, unlike an archived recipe, which is
   * filed away on purpose (PLAN-ARCHIVE §E).
   *
   * **These matchers are the only filter these rows get.** Their items are
   * `forceMount`ed, so cmdk scores neither their value nor any keywords — the
   * matcher a kind's own list searches with is the matcher the palette searches
   * with, and there is no second opinion to disagree with it. That is what lets
   * a journal be found by a word buried in a text block without a page of prose
   * being handed to cmdk on every keystroke.
   */
  const trimmedQuery = query.trim();
  const batchHits = useMemo(() => {
    if (trimmedQuery.length === 0) return [];
    return batches
      .filter((batch) => batchMatchesQuery(batch, trimmedQuery))
      .slice(0, PALETTE_RECIPE_PREVIEW);
  }, [batches, trimmedQuery]);
  const blendHits = useMemo(() => {
    if (trimmedQuery.length === 0) return [];
    return blends
      .filter((blend) => blendMatchesQuery(blend, trimmedQuery))
      .slice(0, PALETTE_RECIPE_PREVIEW);
  }, [blends, trimmedQuery]);
  const journalHits = useMemo(() => {
    if (trimmedQuery.length === 0) return [];
    // Without the reference names: a journal found by the name of the batch it
    // is about would sit under Journals while that batch sits under Batches,
    // and the brewer who typed the batch's name meant the batch.
    return journals
      .filter((journal) => journalMatchesQuery(journal, trimmedQuery))
      .slice(0, PALETTE_RECIPE_PREVIEW);
  }, [journals, trimmedQuery]);

  /**
   * Whether anything was found by a matcher rather than by cmdk.
   *
   * `CommandEmpty` asks cmdk how many rows survived *its* filter, and a
   * `forceMount`ed row is not registered with it at all — so a query that finds
   * only a batch leaves that count at zero and "No matches." would print above
   * the batch it just found. The empty state is therefore ours to withhold.
   */
  const forcedHits = batchHits.length + blendHits.length + journalHits.length;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Chrome's autofill dispatches a keydown with no key, so the modifier is
      // checked first, as the shell's shortcuts do.
      if ((event.metaKey || event.ctrlKey) && event.key?.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((previous) => !previous);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // The button's way in. It opens rather than toggles: a button that closes the
  // dialog covering it is a button nobody can reach.
  useEffect(() => {
    const onRequest = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, onRequest);
    return () => window.removeEventListener(OPEN_EVENT, onRequest);
  }, []);

  function run(action: () => void): void {
    setOpen(false);
    setQuery('');
    action();
  }

  const nextUnitSystem = settings.unitSystem === 'us' ? 'metric' : 'us';

  return (
    <CommandDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
      title="Command palette"
      description="Jump to a recipe, batch, blend or journal, create one, or run a command."
    >
      <CommandInput
        placeholder="Search recipes, batches, blends, journals and commands…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {forcedHits === 0 && <CommandEmpty>No matches.</CommandEmpty>}

        {recipeHits.length > 0 && (
          <CommandGroup heading="Recipes">
            {recipeHits.map((recipe) => (
              <CommandItem
                key={recipe.id}
                value={recipeCommandValue(recipe)}
                keywords={[...recipeCommandKeywords(recipe)]}
                onSelect={() =>
                  run(() => {
                    void navigate({
                      to: '/recipes/$recipeId',
                      params: { recipeId: recipe.id },
                    });
                  })
                }
              >
                <FlaskConicalIcon />
                <span className="truncate" {...userContent}>
                  {recipe.name}
                </span>
                {recipe.tags.length > 0 && (
                  <span className="ml-auto truncate text-xs text-muted-foreground" {...userContent}>
                    {recipe.tags.join(', ')}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/*
          `forceMount` on the group and on every row in it: the hits above were
          chosen by the brew log's own matcher, and cmdk is not asked for a
          second opinion. It is needed in both places — a forced *item* opts out
          of cmdk's registry, which is exactly what would otherwise leave the
          group out of `filtered.groups` and hide the whole thing while a search
          is running. Arrow keys and Enter are unaffected: cmdk walks the DOM
          for those, not its registry.
        */}
        {batchHits.length > 0 && (
          <CommandGroup heading="Batches" forceMount>
            {batchHits.map((batch) => (
              <CommandItem
                key={batch.id}
                forceMount
                value={commandValue('batch', batch.id, batch.name)}
                onSelect={() =>
                  run(() => {
                    void navigate({
                      to: '/batches/$batchId',
                      params: { batchId: batch.id },
                    });
                  })
                }
              >
                <CalendarCheckIcon />
                <span className="truncate" {...userContent}>
                  {batch.name}
                </span>
                {/* Ours, not the brewer's — but it sits where a recipe's tags
                    sit, so it carries the same attribute for the same reason
                    the row above it does. */}
                <span className="ml-auto truncate text-xs text-muted-foreground" {...userContent}>
                  {BATCH_STATUS_LABELS[batch.status]}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {blendHits.length > 0 && (
          <CommandGroup heading="Blends" forceMount>
            {blendHits.map((blend) => (
              <CommandItem
                key={blend.id}
                forceMount
                value={commandValue('blend', blend.id, blend.name)}
                onSelect={() =>
                  run(() => {
                    void navigate({
                      to: '/blending/$blendId',
                      params: { blendId: blend.id },
                    });
                  })
                }
              >
                <BlendIcon />
                <span className="truncate" {...userContent}>
                  {blend.name}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {journalHits.length > 0 && (
          <CommandGroup heading="Journals" forceMount>
            {journalHits.map((journal) => (
              <CommandItem
                key={journal.id}
                forceMount
                value={commandValue('journal', journal.id, journalTitle(journal))}
                onSelect={() =>
                  run(() => {
                    void navigate({
                      to: '/journals/$journalId',
                      params: { journalId: journal.id },
                    });
                  })
                }
              >
                <NotebookPenIcon />
                <span className="truncate" {...userContent}>
                  {journalTitle(journal)}
                </span>
                {journal.tags.length > 0 && (
                  <span className="ml-auto truncate text-xs text-muted-foreground" {...userContent}>
                    {journal.tags.join(', ')}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandGroup heading="Actions">
          <CommandItem
            value="ask assistant open chat help"
            onSelect={() =>
              run(() => {
                assistant.setOpen(true);
              })
            }
          >
            <AssistantOrb />
            <span>Ask the assistant…</span>
            <CommandShortcut>{cmd('J')}</CommandShortcut>
          </CommandItem>

          <CommandItem
            value="new recipe create"
            onSelect={() =>
              run(() => {
                // The route does the creating, not the palette (see
                // `routes/recipes/new.tsx`): the palette can be opened over
                // the recipe list, and a recipe written from here would paint
                // a row onto the list behind the dialog before the editor
                // arrived. No ticket — the palette is not a list, and wherever
                // it was opened is still the entry behind this one.
                void navigate({ to: '/recipes/new' });
              })
            }
          >
            <PlusIcon />
            <span>New recipe</span>
          </CommandItem>

          <CommandItem value="undo" disabled={!history.canUndo} onSelect={() => run(undo)}>
            <Undo2Icon />
            <span className="truncate">
              Undo{history.undoLabel === null ? '' : ` — ${history.undoLabel}`}
            </span>
            <CommandShortcut>{cmd('Z')}</CommandShortcut>
          </CommandItem>

          <CommandItem value="redo" disabled={!history.canRedo} onSelect={() => run(redo)}>
            <Redo2Icon />
            <span className="truncate">
              Redo{history.redoLabel === null ? '' : ` — ${history.redoLabel}`}
            </span>
            <CommandShortcut>{shiftCmd('Z')}</CommandShortcut>
          </CommandItem>

          <CommandItem value="toggle theme dark light" onSelect={() => run(toggleTheme)}>
            {resolvedTheme === 'dark' ? <SunIcon /> : <MoonIcon />}
            <span>Switch to {resolvedTheme === 'dark' ? 'light' : 'dark'} theme</span>
          </CommandItem>

          <CommandItem
            value="toggle units metric us imperial"
            onSelect={() =>
              run(() => {
                dispatch('settings.update', { unitSystem: nextUnitSystem });
              })
            }
          >
            <RulerIcon />
            <span>Switch to {nextUnitSystem === 'us' ? 'US customary' : 'metric'} units</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Go to">
          {NAV_ITEMS.map(({ to, label, description, icon: Icon }) => (
            <CommandItem
              key={to}
              value={`${label} ${description}`}
              onSelect={() =>
                run(() => {
                  void navigate({ to });
                })
              }
            >
              <Icon className="text-muted-foreground" />
              <span>{label}</span>
              <span className="ml-auto truncate text-xs text-muted-foreground">{description}</span>
            </CommandItem>
          ))}

          {/* Settings by hand, because it is no longer a row in `NAV_ITEMS`:
              the sidebar's account row is the way there now, and the list had
              a labelled row pointing at the same page. A palette is a list of
              everywhere you can go, so it keeps the destination — typed out
              here rather than restored to the nav list. */}
          <CommandItem
            value="Settings Units, theme, account and billing"
            onSelect={() =>
              run(() => {
                void navigate({ to: '/settings' });
              })
            }
          >
            <Settings2Icon className="text-muted-foreground" />
            <span>Settings</span>
            <span className="ml-auto truncate text-xs text-muted-foreground">
              Units, theme, account and billing
            </span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
